import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';
import { Options } from '@/utils/layerzero-options';

import { Token, ChainId, Tokens, GOAT_MAINNET_EID } from '../constants';
import { useGetContractByUserWeb3 } from '@/hooks/use-contract';
import { Config as MetisAdapterConfig } from '@/contracts/metisLayzeroAdapter';
import user from '@/stores/user';
import ModalFunc from '@/utils/components/modal';
import Alert from '@/utils/components/alert';
import useTokenBalance from './use-token-balance';
import { handleError } from '@/utils/error-handler';

// Adapter addresses for different tokens
// You can extend this based on your needs
const getAdapterAddress = (chainId: ChainId): string => {
    return MetisAdapterConfig[chainId]?.address;
};

interface SendParam {
    dstEid: number;
    to: string;
    amountLD: string;
    minAmountLD: string;
    extraOptions: string;
    composeMsg: string;
    oftCmd: string;
}

interface MessagingFee {
    nativeFee: bigint;
    lzTokenFee: bigint;
}

interface EstimateResult {
    fee: MessagingFee;
    feeInEther: string;
    nativeFee: bigint;
}

interface LayerzeroSendParams {
    token: Token;
    amount: BigNumber;
    recipientAddress: string;
    dstEid?: number;
    gasLimit?: number;
    slippage?: number;
}

export default function useLayerzeroAdapter() {
    const getContract = useGetContractByUserWeb3();
    const { chainId } = user;

    // Get native token balance for gas estimation
    const { balance: nativeBalance } = useTokenBalance('');
    const adapterAddress = getAdapterAddress(chainId);

    // Estimate gas and fees for cross-chain transfer
    async function estimateLayerzeroFee(
        params: LayerzeroSendParams,
    ): Promise<EstimateResult | undefined> {
        const {
            token,
            amount,
            recipientAddress,
            dstEid = GOAT_MAINNET_EID,
            gasLimit = 200000,
        } = params;

        if (!user.address || !chainId) {
            console.error('Wallet not connected');
            return undefined;
        }

        const tokenDetail = (Tokens[token] as any)?.[chainId];

        if (!tokenDetail || !adapterAddress) {
            console.error('Token or adapter not found');
            return undefined;
        }

        try {
            const adapterContract = await getContract(
                adapterAddress,
                (MetisAdapterConfig as any)[chainId]?.abi,
                false,
            );
            console.log(adapterContract);

            if (!adapterContract) {
                return undefined;
            }

            // Build send parameters
            const amountLD = amount
                .shiftedBy(tokenDetail.decimals)
                .integerValue()
                .toFixed();

            const slippage = params.slippage || 0; // Default 1% slippage
            const minAmountLD = amount
                .multipliedBy(1 - slippage)
                .shiftedBy(tokenDetail.decimals)
                .integerValue()
                .toFixed();

            // Create extra options
            const extraOptions = Options.newOptions()
                .addExecutorLzReceiveOption(gasLimit, 0)
                .toHex();

            const sendParam: SendParam = {
                dstEid: dstEid,
                to: ethers.zeroPadValue(recipientAddress, 32),
                amountLD: amountLD,
                minAmountLD: minAmountLD,
                extraOptions: '0x',
                composeMsg: '0x',
                oftCmd: '0x',
            };

            console.log('Estimating LayerZero fees with params:', sendParam);

            // Quote the fee (use native token, not LZ token)
            const fee = await adapterContract.quoteSend(sendParam, false);

            console.log('Fee estimate:', {
                nativeFee: fee.nativeFee.toString(),
                lzTokenFee: fee.lzTokenFee.toString(),
            });

            const feeInEther = new BigNumber(fee.nativeFee.toString())
                .shiftedBy(-18)
                .toFixed();

            return {
                fee: {
                    nativeFee: BigInt(fee.nativeFee.toString()),
                    lzTokenFee: BigInt(fee.lzTokenFee.toString()),
                },
                feeInEther: feeInEther,
                nativeFee: BigInt(fee.nativeFee.toString()),
            };
        } catch (e) {
            console.error('Estimate gas error:', e);
            handleError(e);
            return undefined;
        }
    }

    // Send cross-chain transfer
    async function sendToGoat(
        params: LayerzeroSendParams,
    ): Promise<string | false> {
        const {
            token,
            amount,
            recipientAddress,
            dstEid = GOAT_MAINNET_EID,
            gasLimit = 200000,
        } = params;

        if (!user.address || !chainId) {
            Alert({
                type: 'error',
                text: 'Wallet not connected',
            });
            return false;
        }

        const tokenDetail = (Tokens[token] as any)?.[chainId];

        if (!tokenDetail || !adapterAddress) {
            Alert({
                type: 'error',
                text: 'Token or adapter not found',
            });
            return false;
        }

        try {
            // First, estimate the fee
            const estimate = await estimateLayerzeroFee(params);

            if (!estimate) {
                Alert({
                    type: 'error',
                    text: 'Failed to estimate gas',
                });
                return false;
            }

            // Check if user has enough native token for fee
            const feeInNative = new BigNumber(estimate.feeInEther);
            if (feeInNative.isGreaterThan(nativeBalance)) {
                ModalFunc({
                    type: 'sorry',
                    title: 'Sorry',
                    text: `Insufficient native token balance. Need ${feeInNative.toFixed(
                        6,
                    )} for gas, but have ${nativeBalance.toFixed(6)}`,
                    buttonText: 'OK',
                });
                return false;
            }

            const adapterContract = await getContract(
                adapterAddress,
                (MetisAdapterConfig as any)[chainId]?.abi,
                true,
            );

            if (!adapterContract) {
                return false;
            }

            // Build send parameters
            const amountLD = amount
                .shiftedBy(tokenDetail.decimals)
                .integerValue()
                .toFixed();

            const minAmountLD = amountLD;

            const extraOptions = Options.newOptions()
                .addExecutorLzReceiveOption(gasLimit, 0)
                .toHex();

            const sendParam: SendParam = {
                dstEid: dstEid,
                to: ethers.zeroPadValue(recipientAddress, 32),
                amountLD: amountLD,
                minAmountLD: minAmountLD,
                extraOptions: '0x',
                composeMsg: '0x',
                oftCmd: '0x',
            };

            console.log('Sending cross-chain transaction...', sendParam);

            // Send transaction (pay with native token)
            const tx = await adapterContract.send(
                sendParam,
                estimate.fee,
                user.address, // refund address
                {
                    value: estimate.nativeFee.toString(),
                },
            );

            const receipt = await tx.wait();

            if (receipt && receipt.hash) {
                console.log('Transaction confirmed:', receipt.hash);

                return receipt.hash;
            } else {
                return false;
            }
        } catch (e) {
            console.error('Send to GOAT error:', e);
            handleError(e);
            return false;
        }
    }

    return {
        // Methods
        estimateLayerzeroFee,
        sendToGoat,
        // Helpers
        getAdapterAddress: () => getAdapterAddress(chainId),
    };
}
